// @ts-nocheck

import { easeQuadInOut } from 'd3-ease';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { animated, useSpring } from 'react-spring/web.cjs';
import styled, { css, keyframes } from 'styled-components';

import { Trash, ValidStroke } from '../../assets/svg/common';
import { ShareSvg } from '../../assets/svg/history';
import { Target } from '../../client';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { ZIndices } from '../../styled-components/dark/common';
import { BetslipFilters } from '../../types/history';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import BasketTabsContainer from '../BasketTabs/BasketTabsContainer';
import Link from '../Link';

const Wrap = styled.div`
  background-color: ${theme.BasketHeader.backgroundColor};
  border-radius: 10px 10px 0 0;
  position: relative;
  z-index: ${ZIndices.basketHeader};

  ${({ $paddingBottom }) =>
    $paddingBottom
      ? css`
          padding-bottom: 0.5em;
        `
      : undefined}
`;

const SlideMark = styled.div`
  position: absolute;
  top: 10px;
  margin: 0 auto;
  width: 30px;
  height: 4px;
  border-radius: 2px;
  background-color: ${theme.BasketMobileModal.slideMarkBackgroundColor};
  left: calc(50% - 15px);
  z-index: 2;

  ${({ $isResume }) => {
    if ($isResume) {
      return css`
        opacity: 0.6;
      `;
    }
  }}
`;

const CONFIRMATION_POP_ANIM_DURATION = 600;
const CONFIRMATION_INNER_APPARITION_DURATION = 600;

const opacityAnim = keyframes`
  to {
    opacity: 1;
  }
`;

const backgroundAnim = keyframes`
  to {
    background-position: 0% 100%;
  }
`;

const AnimatedRow = styled(animated.div)`
  position: relative;

  padding: 0.5em 0.7em;
  min-height: 1.6em;

  border-top-left-radius: 1em;
  border-top-right-radius: 1em;

  background-image: linear-gradient(to bottom, ${theme.BasketHeader.backgroundColor} 33%, #49e496 66%, #1b8680 100%);
  background-size: 100% 300%;
  background-position: 0% 0%;

  color: white;

  ${Target.__MOBILE__
    ? css`
        padding-top: 1em;
      `
    : null}

  ${({ $hasConfirmation, $shouldAnimate }) => {
    if ($shouldAnimate) {
      return css`
        > * {
          opacity: 0;
          pointer-events: none;
        }

        ${$hasConfirmation
          ? css`
              animation: ${backgroundAnim} cubic-bezier(0.28, 0.84, 0.42, 1) ${CONFIRMATION_POP_ANIM_DURATION}ms
                forwards;

              > * {
                pointer-events: auto;
                animation: ${opacityAnim} ${CONFIRMATION_INNER_APPARITION_DURATION}ms ease-in-out
                  ${CONFIRMATION_POP_ANIM_DURATION}ms forwards;
              }
            `
          : undefined}
      `;
    }
  }}
`;

const Count = styled.span`
  color: ${theme.BasketHeader.selectionColor};
  font-weight: bold;
  font-size: 120%;
`;

const Element = styled.span`
  color: ${theme.BasketHeader.defaultColor};
  font-size: 90%;
  display: flex;
  align-items: baseline;
`;

const TrashIcon = styled(Trash)`
  display: inline-block;
  color: ${theme.BasketHeader.defaultColor};
  height: 1.3em;
  width: 1.3em;
  cursor: pointer;
`;

const ShareIcon = styled(ShareSvg)`
  width: 1.25em;
  height: 1.25em;
  float: right;
  cursor: pointer;
`;

const StyledTabsContainer = styled(BasketTabsContainer)`
  margin: 0 0.6em;
  width: calc(100% - 1.2em);
`;

const CompleteLabel = styled(Element)`
  text-transform: uppercase;
  font-size: 1em;
`;

const BetConfirmationLeft = styled.div`
  max-width: 30%;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  overflow: hidden;
  font-size: 70%;
`;

const BetConfirmationCenter = styled.div`
  position: absolute;
  width: calc(100% - 1.4em);

  text-transform: uppercase;
  font-size: 110%;

  pointer-events: none;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const strokeAnim = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

const BetConfirmationCenterIcon = styled(ValidStroke)`
  width: 1.3em;
  height: 1.3em;
  margin-right: 0.35em;

  > circle {
    stroke-dasharray: 80;
    stroke-dashoffset: 80;
  }

  > path {
    stroke-dasharray: 15;
    stroke-dashoffset: 15;
  }

  ${({ $hasConfirmation }) => {
    if ($hasConfirmation) {
      return css`
        > path,
        > circle {
          animation: ${strokeAnim} 600ms ease-in-out
            ${CONFIRMATION_POP_ANIM_DURATION + CONFIRMATION_INNER_APPARITION_DURATION}ms forwards;
        }
      `;
    }
  }}
`;

const BetConfirmationCenterLabel = styled.div`
  font-weight: 600;
  font-size: 85%;
`;

const Content = styled.div`
  position: relative;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

type PropsType = {
  className?: string;
  nbSelection?: number;
  onTrashIconClick?: (...params: unknown) => unknown;
  isCashout: boolean;
  isResume: boolean;
  betshareBetslipReference?: string;
  hasConfirmation?: boolean;
};

const BasketHeader = ({
  className,
  nbSelection = null,
  onTrashIconClick,
  isCashout,
  isResume,
  betshareBetslipReference,
  hasConfirmation = false,
}: PropsType) => {
  const isEmpty = useMemo(() => !nbSelection, [nbSelection]);
  const confirmationRowRef = useRef();
  const shouldAnimate = useMemo(() => isResume, [isResume]);

  const renderSelections = useCallback(() => {
    if (nbSelection === null) {
      return null;
    }
    if (isCashout) {
      return <CompleteLabel>{__('Complete bet')}</CompleteLabel>;
    }
    return (
      <Element $isEmpty={isEmpty}>
        <Count>{nbSelection}</Count>&nbsp;
        {__('selection', nbSelection, 'selections')}
      </Element>
    );
  }, [nbSelection, isCashout, isEmpty]);

  const displayedBetReference = useMemo(() => {
    const [, second] = (betshareBetslipReference ?? '').split('-');

    return second ? second : betshareBetslipReference;
  }, [betshareBetslipReference]);

  const [style, animate] = useSpring(
    () => ({
      config: {
        duration: CONFIRMATION_POP_ANIM_DURATION,
        easing: easeQuadInOut,
      },
    }),
    [],
  );

  useEffect(() => {
    if (shouldAnimate) {
      const confirmationRowNode = confirmationRowRef.current;

      if (confirmationRowNode) {
        if (hasConfirmation) {
          animate({
            height: Target.__MOBILE__ ? 'auto' : '2.85em',
          });
        } else {
          animate({
            height: '2.85em',
          });
        }
      }
    } else {
      animate({
        height: '2.85em',
      });
    }
  }, [animate, hasConfirmation, shouldAnimate]);

  const displayTabs = useMemo(() => !isCashout && !isResume && !isEmpty, [isCashout, isEmpty, isResume]);

  return (
    <Wrap className={className} $paddingBottom={displayTabs}>
      {Target.__MOBILE__ ? <SlideMark $isResume={isResume} /> : null}

      <AnimatedRow
        ref={confirmationRowRef}
        style={style}
        $hasConfirmation={hasConfirmation}
        $shouldAnimate={shouldAnimate}
      >
        {isResume ? (
          <Content>
            <BetConfirmationLeft>{displayedBetReference}</BetConfirmationLeft>
            <BetConfirmationCenter>
              <BetConfirmationCenterIcon $hasConfirmation={hasConfirmation} />
              <BetConfirmationCenterLabel>{__('Bet validated!')}</BetConfirmationCenterLabel>
            </BetConfirmationCenter>
            {betshareBetslipReference ? (
              <Link to={RoutesRedirectionFactory.getHistoryRoutePathname(BetslipFilters.RUNNING)}>
                <ShareIcon />
              </Link>
            ) : null}
          </Content>
        ) : (
          <Content>
            {renderSelections()}
            {nbSelection && onTrashIconClick ? <TrashIcon onClick={onTrashIconClick} /> : null}
          </Content>
        )}
      </AnimatedRow>
      {displayTabs ? <StyledTabsContainer /> : null}
    </Wrap>
  );
};

export default BasketHeader;
